import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { initGA } from '@/lib/analytics';

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    initGA();
    setShowBanner(false);
  };

  const declineCookies = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setShowBanner(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          data-testid="cookie-consent-banner"
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto bg-card border border-border shadow-lg rounded-xl p-4 md:p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Cookie className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-foreground mb-1">
                    We use cookies
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    We use Google Analytics cookies to understand how visitors use our site.
                    No cookies are set unless you click "Accept". See{' '}
                    <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                      how Google uses your data
                    </a>{' '}
                    and our{' '}
                    <a href="/privacy" className="text-accent hover:underline">
                      Privacy Policy
                    </a>.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 w-full md:w-auto">
                <Button
                  variant="outline"
                  onClick={declineCookies}
                  data-testid="cookie-decline-btn"
                  className="flex-1 md:flex-none rounded-full"
                >
                  Decline
                </Button>
                <Button
                  onClick={acceptCookies}
                  data-testid="cookie-accept-btn"
                  className="flex-1 md:flex-none bg-accent text-accent-foreground hover:bg-accent/90 rounded-full"
                >
                  Accept
                </Button>
              </div>
              <button
                onClick={declineCookies}
                className="absolute top-2 right-2 md:static p-1 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
